/**
 * Een antwoord van de API dat niet 2xx was.
 *
 * `status` staat erbij omdat de UI op 404 iets anders moet zeggen dan op de
 * rest: een 404 op een gast-endpoint betekent "je link klopt niet meer", niet
 * "dit feest bestaat niet". Dat verschil is aan de serverkant met opzet
 * onzichtbaar gemaakt, dus de client mag het ook niet uit elkaar halen.
 */
export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

interface ProblemDetails {
  title?: string
  detail?: string
  errors?: Record<string, string[]>
}

interface RequestOptions {
  slug: string
  method?: string
  body?: unknown
  signal?: AbortSignal
}

/**
 * Elk verzoek naar een gast-endpoint.
 *
 * Hier ging een invite token als header mee. Dat is eruit: de link ís de
 * toegang, dus de slug in het pad is alles wat de server nodig heeft.
 */
export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {}

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (!response.ok) {
    throw new ApiError(response.status, await describe(response))
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

/** Haalt de nuttigste regel uit een ProblemDetails, met een val terug. */
async function describe(response: Response): Promise<string> {
  try {
    const problem = (await response.json()) as ProblemDetails

    const firstFieldError = problem.errors ? Object.values(problem.errors).flat().at(0) : undefined

    return firstFieldError ?? problem.detail ?? problem.title ?? `Fout ${response.status}`
  } catch {
    return `Fout ${response.status}`
  }
}

/** Bouwt een querystring en laat lege waarden weg. */
export function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value))
    }
  }

  const rendered = search.toString()

  return rendered ? `?${rendered}` : ''
}
